import React, { useEffect, useState } from 'react';
import Page from "components/page/Page";
import { useDispatch } from 'react-redux';
import { Prompt, useHistory, useParams } from 'react-router';
import { t } from 'i18next';
import { GetAudience, GetAudienceCustomers, GetAudienceCustomersExport, IAudience, IAudienceCustomer, IAudienceFilter, PostAudience, PutAudience } from 'api/rpc/2024-04/facilityAdmin/marketing/emailCampaigns';
import { StatusCode } from 'api/protocols';
import { showError, showSuccess } from 'redux/actions/ui';
import Card from 'components/card/Card';
import FormLayout from 'components/form/FormLayout';
import Input from 'components/form/input';
import { Badge } from "components/badge/Badge";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { ButtonNew } from 'components/buttonNew';
import _ from 'lodash';
import SelectNew, { TSelectOption } from 'components/select/SelectNew';
import AudienceCustomersTable from './AudienceCustomersTable';
import { GetCustomerType } from 'api/rpc/facilityAdmin/customerType';
import { downloadCsv } from 'helpers/Helpers';
import { TCustomerTag, TCustomerType } from 'redux/reducers/models/customer';
import { useUrlParams } from 'hooks/useUrlParams/useUrlParams';
import useCancelToken from 'hooks/useCancelToken/useCancelToken';
import { CancelToken } from 'axios';
import { GetMembership, TFacilityMembership } from 'api/rpc/2024-04/facilityAdmin/membership/membership';
import { GetLeague } from 'api/rpc/2024-04/facilityAdmin/league/league';
import { ILeague } from 'redux/reducers/models/league';
import { ITournament } from 'redux/reducers/models/tournament';
import { GetTournament } from 'api/rpc/2024-04/facilityAdmin/tournament/tournament';
import { GetFacility } from 'api/rpc/2024-04/facility';
import { IFacility } from 'redux/reducers/models/facility';
import { GetTag } from 'api/rpc/2024-04/facilityAdmin/client/tags';

interface IParams {
  id?: string;
}

type TFilterFields =
  'league_roster' |
  'tournament_roster' |
  'home_facility' |
  'membership' |
  'customer_tag' |
  'address' |
  'customer_type' |
  'customer_cart_type';
type TFilterOperators = 'is' | 'is_not' | 'contains' | 'does_not_contain';

export default function Audience() {
  const { searchParams } = useUrlParams({ campaign_id: '' });
  const history = useHistory();
  const dispatch = useDispatch();
  const { id } = useParams<IParams>();
  const isEdit = id != null;
  const [audience, setAudience] = useState<IAudience>({
    id: 0,
    title: '',
    imported_customer_ids: [],
    filters: [],
    created_at: '',
    updated_at: '',
  });
  const filters = audience.filters;
  const fields: TFilterFields[] = [
    'league_roster',
    'tournament_roster',
    'home_facility',
    'membership',
    'customer_tag',
    'customer_type',
    'customer_cart_type',
  ];
  const operators: TFilterOperators[] = [
    'contains',
    'does_not_contain',
  ];
  const [customers, setCustomers] = useState<IAudienceCustomer[]>([]);
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [errors, setErrors] = useState<{ [key: string]: string[] }>({});
  const { cancelToken, cancelRequest } = useCancelToken();

  const [loadingTable, setLoadingTable] = useState(false);

  useEffect(() => {
    function handleBeforeUnload(event: BeforeUnloadEvent) {
      if (hasChanges && isEdit) {
        event.preventDefault();
        return "";
      }

      return undefined;
    }

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasChanges]);

  useEffect(() => {
    void loadFilterValues();

    if (!isEdit) {
      void create();
    } else {
      void loadAudience();
    }
  }, []);

  const [customerCartTypes, setCustomerCartTypes] = useState<TCustomerType[]>([]);
  const [customerTypes, setCustomerTypes] = useState<TCustomerType[]>([]);
  const [memberships, setMemberships] = useState<TFacilityMembership[]>([]);
  const [leagues, setLeagues] = useState<ILeague[]>([]);
  const [tournaments, setTournaments] = useState<ITournament[]>([]);
  const [facilities, setFacilities] = useState<IFacility[]>([]);
  const [customerTags, setCustomerTags] = useState<TCustomerTag[]>([]);

  async function loadFilterValues() {
    const tasks = [];

    tasks.push(GetCustomerType({}).then(resp => {
      const data: TCustomerType[] = resp.data;
      setCustomerTypes(data.filter(t => t.application === 'green_fee'));
      setCustomerCartTypes(data.filter(t => t.application === 'power_cart'));
    }));

    tasks.push(GetMembership({ archived: false }, false).then(resp => setMemberships(resp.data)));
    tasks.push(GetLeague({}, false).then(resp => setLeagues(resp.data)));
    tasks.push(GetTournament({}, false).then(resp => setTournaments(resp.data)));
    tasks.push(GetFacility('', false).then(resp => setFacilities(resp.data)));
    tasks.push(GetTag({type: 'customer'}, false).then(resp => setCustomerTags(resp.data)));

    await Promise.all(tasks);
  }

  async function loadAudience() {
    const resp = await GetAudience({ id }, false);

    if (resp.status !== StatusCode.OK) {
      dispatch(showError(resp.message));
      return;
    }

    setAudience(resp.data);
  }

  function save() {
    if (isEdit) {
      void update();
    } else {
      void create();
    }
  }

  async function update() {
    const resp = await PutAudience({
      ...audience,
    }, false);

    if (resp.status !== StatusCode.OK) {
      const errors: { [key: string]: string[] } = resp.data as any;
      const firstErrorKey = Object.keys(errors)[0];
      const firstErrorMessage = errors[firstErrorKey];
      dispatch(showError(firstErrorMessage[0] ?? ''));
      setErrors(errors);
      return;
    }

    dispatch(showSuccess(t('Updated smart list')));

    setAudience(resp.data);
    setHasChanges(false);
    setErrors({});
  }

  async function create() {
    const resp = await PostAudience({
      ...audience,
      filters: filters,
      customer_ids: customers.map(c => c.id),
      title: audience.title == '' ? 'New Smart List' : audience.title,
    }, false);

    if (resp.status !== StatusCode.OK) {
      dispatch(showError(resp.message));
      return;
    }

    setAudience(resp.data);
    setHasChanges(false);

    dispatch(showSuccess(t('Created audience')));
    history.push('/admin/marketing/email-campaigns/smart-list/' + resp.data.id + '?' + searchParams.toString());
  }

  function setFilters(newFilters: IAudience['filters']) {
    setHasChanges(true);
    setCustomerTableState({
      ...customerTableState, 
      offset: 0,
    });
    setAudience({
      ...audience,
      filters: newFilters,
    });
  }

  function addFilter() {
    setFilters(filters.concat([{
      field: '',
      operator: 'contains',
      value: [],
    }]));
  }

  function removeFilter(i: number) {
    setFilters(filters.slice(0, i).concat(filters.slice(i + 1)));
  }

  function handleSelectedField(i: number, value: string) {
    const filter = filters[i];
    const newFilter: IAudienceFilter = {
      ...filter,
      field: value,
    };
    const updatedFilters = filters.slice(0, i)
      .concat([newFilter])
      .concat(filters.slice(i + 1));

    setFilters(updatedFilters);

    removeErrorState(`filters.${i}.field`);
  }

  function handleSelectedOperator(i: number, value: string) {
    const filter = filters[i];
    const newFilter: IAudienceFilter = {
      ...filter,
      operator: value,
    };
    const updatedFilters = filters.slice(0, i)
      .concat([newFilter])
      .concat(filters.slice(i + 1));

    setFilters(updatedFilters);

    removeErrorState(`filters.${i}.operator`);
  }

  function handleFilterValueSelected(i: number, value: string | number) {
    const filter = filters[i];
    const valueIndex = filter.value.indexOf(value);
    let filterValues: (string | number)[] = [];

    if (valueIndex >= 0) {
      filterValues = filter.value.slice(0, valueIndex).concat(filter.value.slice(valueIndex + 1));
    } else {
      filterValues = filter.value.concat([value]);
    }
    const newFilter: IAudienceFilter = {
      ...filter,
      value: filterValues,
    };
    const updatedFilters = filters.slice(0, i).concat([newFilter]).concat(filters.slice(i + 1));
    setFilters(updatedFilters);

    removeErrorState(`filters.${i}.value`);
  }

  function removeErrorState(errorKey: string) {
    const updatedErrors = {
      ...errors,
    };
    delete updatedErrors[errorKey];
    setErrors(updatedErrors);
  }

  const [counts, setCounts] = useState({
    includedCount: 0,
    excludedCount: 0,
  });
  const [total, setTotal] = useState(0);
  const [customerTableState, setCustomerTableState] = useState({
    limit: 15,
    offset: 0,
    search: '',
  });

  async function loadAudienceCustomers(token: CancelToken) {
    if (audience.id == 0) {
      return;
    }

    const page = Math.floor(customerTableState.offset / customerTableState.limit) + 1;

    setLoadingTable(true);
    const resp = await GetAudienceCustomers(
      {
        id: audience.id,
        page,
        search: customerTableState.search,
        has_filters: true,
        filters,
        imported_customer_ids: audience.imported_customer_ids,
        removed_customer_ids: audience.removed_customer_ids,
      },
      false,
      token,
    );
    setLoadingTable(false);

    if (resp.status !== StatusCode.OK) {
      if (token && token.reason) {
        return;
      }
      dispatch(showError(resp.message));
      return;
    }

    setCustomers(resp.data.customers.data);
    setTotal(resp.data.customers.total);
    setCounts({
      excludedCount: resp.data.excluded_count,
      includedCount: resp.data.included_count,
    });
  }

  useEffect(() => {
    if (audience.id == 0) {
      return;
    }

    cancelRequest();
    void loadAudienceCustomers(cancelToken());
  }, [filters, audience.imported_customer_ids, customerTableState]);

  function onPageChange(dir: 'next' | 'prev', limit: number, offset: number) {
    if (dir === 'next') {
      setCustomerTableState({ ...customerTableState, offset: offset + limit });
    } else {
      setCustomerTableState({ ...customerTableState, offset: offset - limit });
    }
  }

  function handleSearch(search: string) {
    setCustomerTableState({ ...customerTableState, offset: 0, search });
  }

  function filterValuePlaceholder(filter: IAudienceFilter): string {
    let placeholder = t('filter by...');

    switch (filter.field as (TFilterFields | '')) {
      case 'membership':
        placeholder = memberships.filter(m => filter.value.includes(m.id)).map<string>(m => m.title).join(', ');
        break;
      case 'customer_type':
        placeholder = customerTypes.filter(c => filter.value.includes(c.id)).map(c => c.title).join(', ');
        break;
      case 'customer_cart_type':
        placeholder = customerCartTypes.filter(c => filter.value.includes(c.id)).map(c => c.title).join(', ');
        break;
      case 'league_roster':
        placeholder = leagues.filter(l => filter.value.includes(l.id)).map(l => l.name).join(', ');
        break;
      case 'tournament_roster':
        placeholder = tournaments.filter(t => filter.value.includes(t.id)).map(t => t.name).join(', ');
        break;
      case 'home_facility':
        placeholder = facilities.filter(f => filter.value.includes(f.id)).map(f => f.long_name).join(', ');
        break;
      case 'customer_tag':
        placeholder = customerTags.filter(c => filter.value.includes(c.id)).map(c => c.label).join(', ');
        break;
      case '':
        break;
      default:
        console.error('unsupported filter field: ' + filter.field);
    }

    return placeholder.length > 0 ? placeholder : t('filter by...');
  }

  function filterValueOptions(filter: IAudienceFilter) {
    let options: TSelectOption<IAudienceFilter>[] = [];

    switch (filter.field as (TFilterFields | '')) {
      case 'membership':
        options = memberships.map(m => ({
          id: m.id,
          label: m.title,
        }));
        break;
      case 'customer_type':
        options = customerTypes.map(t => ({
          id: t.id,
          label: t.title,
        }));
        break;
      case 'customer_cart_type':
        options = customerCartTypes.map(t => ({
          id: t.id,
          label: t.title,
        }));
        break;
      case 'league_roster':
        options = leagues.map(l => ({
          id: l.id,
          label: l.name,
        }));
        break;
      case 'tournament_roster':
        options = tournaments.map(l => ({
          id: l.id,
          label: l.name,
        }));
        break;
      case 'home_facility':
        options = facilities.map(l => ({
          id: l.id,
          label: l.long_name,
        }));
        break;
      case 'customer_tag':
        options = customerTags.map(l => ({
          id: l.id,
          label: l.label,
        }));
        break;
      case '':
        break;

      default:
        console.error('unsupported filter value: ' + filter.field);
    }

    return options;
  }

  async function handleExport() {
    const resp = await GetAudienceCustomersExport({ id: audience.id });
    downloadCsv(resp.data, _.snakeCase(audience.title) + '.csv');
  }

  function handleImport(newCustomers: IAudienceCustomer[], importType: 'append' | 'replace') {
    const newList = importType === 'append'
      ? audience.imported_customer_ids.concat(newCustomers.map(c => c.id))
      : newCustomers.map(c => c.id);

    setHasChanges(true);
    setAudience({
      ...audience,
      imported_customer_ids: _.uniq(newList),
    });
  }

  function customerStatus(customer: IAudienceCustomer): IAudienceCustomer['status'] {
    if (!customer.preference_marketing_email) {
      return 'unsubscribed';
    }

    if (customer.excluded) {
      return 'excluded';
    }

    return 'active';
  }

  function handleIncludeToggle(customer: IAudienceCustomer) {
    let updated: IAudienceCustomer[] = customers;

    const targetIndex = updated.findIndex(c => customer.id === c.id);

    const excluded = !customer.excluded;
    const updatedCustomer: IAudienceCustomer = {
      ...customer,
      excluded: excluded,
    };
    updatedCustomer.status = customerStatus(updatedCustomer);

    updated = updated.slice(0, targetIndex)
      .concat([updatedCustomer])
      .concat(updated.slice(targetIndex + 1));

    setHasChanges(true);
    setCustomers(updated);
    setCounts({
      ...counts,
      includedCount: excluded ? counts.includedCount - 1 : counts.includedCount + 1,
    });
    setAudience({
      ...audience,
      removed_customer_ids: updated.filter(c => c.excluded).map(c => c.id),
    });
  }

  return <Page
    narrow
    title={isEdit ? t('Edit Smart List') : t('Create New Smart List')}
    subtitle={t('Create a reusable list of customers for future campaigns.')}
    primaryAction={{
      action: save,
      content: t('Save'),
    }}
    breadcrumbs={[
      !searchParams.has('campaign_id') || searchParams.get('campaign_id') == ''
        ? {
          prefix: true,
          label: t('Back'),
          url: '/admin/marketing/email-marketing',
        }
        : {
          prefix: true,
          label: t('Back to campaign builder'),
          url: '/admin/marketing/email-campaigns/' + searchParams.get('campaign_id') + '/new?step=2',
        },
    ]}
  >
    <Prompt
      when={hasChanges}
      message={t('You still have unsaved changes. Continuing will discard all your updates. Are you sure you wish to continue?')}
    />

    <Card>
      <Card.Section>
        <FormLayout>
          <FormLayout.Group>
            <div className="mt-2">
              <Input
                label={t("Name your smart list")}
                placeholder={t("i.e. Members from British Columbia, etc.")}
                value={audience.title}
                onChange={e => {
                  setAudience({ ...audience, title: e.target.value });
                  setHasChanges(true);
                }}
              />
            </div>
          </FormLayout.Group>
        </FormLayout>
      </Card.Section>

      <Card.Section>
        <>
          <div className="text-sm font-medium text-gray-700 mb-2 lg:mb-4">
            {t('Filter by')}:
          </div>
          <FormLayout>
            {filters.map((filter, i) => <>
              {i !== 0 &&
                <FormLayout.Group key={'filter-and-' + i.toString()}>
                  <div className="flex">
                    <Badge type="primary">
                      {t('And')}
                    </Badge>
                  </div>
                </FormLayout.Group>
              }
              <FormLayout.Group key={'filter-group-' + i.toString()}>
                <div className="grid grid-cols-[260px_150px_minmax(150px,_1fr)_70px] gap-6 items-center">
                  <SelectNew
                    id={`filter-field-${i}`}
                    placeholder={t('Filter by...  ')}
                    value={filter.field}
                    onChange={val => handleSelectedField(i, val)}
                    options={fields.map(field => ({
                      id: field,
                      label: _.startCase(field),
                    }))}
                    error={Object.keys(errors).includes(`filters.${i}.field`)}
                    searchable={100}
                  />
                  <SelectNew
                    id={`filter-operator-${i}`}
                    value={filter.operator}
                    onChange={val => handleSelectedOperator(i, val)}
                    options={operators.map(operator => ({
                      id: operator,
                      label: _.startCase(operator),
                    }))}
                    error={Object.keys(errors).includes(`filters.${i}.operator`)}
                  />

                  <SelectNew
                    id={`filter-value-${i}`}
                    placeholder={filterValuePlaceholder(filter)}
                    multiValue={filter.value}
                    onChange={val => {
                      handleFilterValueSelected(i, val);
                    }}
                    options={filterValueOptions(filter)}
                    error={Object.keys(errors).includes(`filters.${i}.value`)}
                    searchable={100}
                  />
                  <ButtonNew type="tertiary" className="px-0" onClick={() => removeFilter(i)}>
                    {t('Remove')}
                  </ButtonNew>
                </div>
              </FormLayout.Group>
            </>,
            )}
            <FormLayout.Group key="add-filter-row">
              <ButtonNew type="tertiary" className="px-0" onClick={addFilter}>
                <FontAwesomeIcon icon={faPlus} className="mr-2" />
                {t('Add Filter')}
              </ButtonNew>
            </FormLayout.Group>
          </FormLayout>
        </>
      </Card.Section>
      <Card.Section>
        <AudienceCustomersTable
          customers={customers}
          onCustomerSearch={handleSearch}
          onPageChange={onPageChange}
          limit={customerTableState.limit}
          offset={customerTableState.offset}
          includedCount={counts.includedCount}
          total={total}
          onImport={handleImport}
          onExport={handleExport}
          onIncludeToggle={handleIncludeToggle}
          loading={loadingTable}
        />
      </Card.Section>
    </Card>
  </Page>;
}
